import { useEffect, useState } from "react";

/** Default interval between line changes while rotating. */
export const STREAMING_QUIP_ROTATION_INTERVAL_MS = 8000;

/** Built-in lines for streaming status UI. */
const STREAMING_QUIP_MESSAGES = [
  "Grabbed coffee… then remembered I already had coffee. Anyway: brewing anyway…",
  "Burned toast. Opened window. Answer getting fresh air too…",
  "Sat on my headphones. Now we're listening… carefully…",
  "Spilled a little water on the counter—now drying hands and thoughts…",
  "Elevator skipped my floor twice. Taking the stairs of coherence…",
  "Microwave's doing its optimistic beep—almost ready…",
  "Autocorrect suggested a fancier word—thanks, but we're keeping this plain…",
  "Pocket-check ritual engaged… keys were already there the whole time…",
  "Opened the fridge like it might contain inspiration—it mostly contained yogurt…",
  "Cloud cover rolled in for ten seconds—dramatic lighting while I wrap this up…",
] as const;

const pickRandom = (messages: readonly string[]): string => {
  const index = Math.floor(Math.random() * messages.length);
  return messages[index] as string;
};

interface UseRandomMessageRotationOptions {
  enabled: boolean;
  intervalMs?: number;
  messages?: readonly string[];
}

/**
 * Returns a random line from {@link STREAMING_QUIP_MESSAGES} (or `messages`), updating on each interval while `enabled`.
 * Returns `null` when disabled or when `messages` is empty.
 */
const useRandomMessageRotation = (options: UseRandomMessageRotationOptions) => {
  const {
    enabled,
    intervalMs = STREAMING_QUIP_ROTATION_INTERVAL_MS,
    messages = STREAMING_QUIP_MESSAGES,
  } = options;
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || messages.length === 0) {
      setMessage(null);
      return;
    }

    setMessage(pickRandom(messages));
    const id = window.setInterval(() => {
      setMessage(pickRandom(messages));
    }, intervalMs);

    return () => {
      window.clearInterval(id);
    };
  }, [enabled, intervalMs, messages]);

  return message;
};

export default useRandomMessageRotation;
